import {
  useState,
  type ChangeEvent,
  type Dispatch,
  type FormEvent,
  type SetStateAction,
} from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { FcGoogle } from "react-icons/fc";
import {
  getAuth,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithEmailAndPassword,
} from "firebase/auth";
import "./ConnectForm.css";
import { apiCall } from "../api/apiCall";
import TokenStore from "../api/tokenStore";
import UserIdStore from "../api/userIdStore";
import { firebaseApp } from "../firebase";
import type { User } from "../models/user";
import generateToastContainer from "../utils/ToastContainer";
import { useTranslation } from "../../src/i18n/UseTranslation";
import type { Language } from "../i18n/UseTranslation";

type AdminRole = "admin" | "super_admin" | "accountant";

type LoginResponse = {
  token: string | null;
  user?: { id: string; role: AdminRole | string };
  message?: string;
};

const ALLOWED_ADMIN_ROLES: AdminRole[] = ["admin", "super_admin", "accountant"];

function ConnectForm() {
  const navigate = useNavigate();
  const { t, language, setLanguage, availableLanguages } = useTranslation();

  const [login, setLogin]       = useState<string>("");
  const [password, setPassword] = useState<string>("");

  const [theme, setTheme] = useState<"light" | "dark">(
    () => (localStorage.getItem("app_theme") as "light" | "dark") ?? "light"
  );

  const toggleTheme = () => {
    const next = theme === "light" ? "dark" : "light";
    setTheme(next);
    localStorage.setItem("app_theme", next);
    document.documentElement.setAttribute("data-theme", next);
  };

  const auth = getAuth(firebaseApp);

  const isAllowedAdminRole = (role: string | undefined): role is AdminRole =>
    Boolean(role && ALLOWED_ADMIN_ROLES.includes(role as AdminRole));

  const clearAuthentication = () => {
    TokenStore.clearToken();
    UserIdStore.clearUserId();
  };


  const emailLoginMutation = useMutation<LoginResponse, Error>({
    mutationKey: ["email-login"],
    mutationFn: async () => {
      const credential = await signInWithEmailAndPassword(
        auth,
        login.trim().toLowerCase(),
        password
      );
      const idToken = await credential.user.getIdToken();

      const response = await apiCall<LoginResponse>("/login/user", {
        method: "POST",
        body: JSON.stringify({ token: idToken }),
      });


      if (!isAllowedAdminRole(response.user?.role)) {
        throw new Error(t.auth.noAdminAccess);
      }

      return response;
    },
    onError: (error) => {
      clearAuthentication();
      toast.error(error.message || t.auth.loginError);
    },
  });

  const fetchMeMutation = useMutation<User, Error, string>({
    mutationKey: ["fetch-me"],
    mutationFn: async (idToken: string): Promise<User> => {
      TokenStore.setToken(idToken);
      return apiCall<User>("/users/me", { method: "GET" });
    },
    onSuccess: (user) => {
      if (!isAllowedAdminRole(user.role?.name)) {
        clearAuthentication();
        toast.error(t.auth.noAdminAccess);
        return;
      }
      UserIdStore.setUserId(user.firebaseUid);
      navigate("/dashboard");
    },
    onError: (error) => {
      clearAuthentication();
      toast.error(error.message || t.auth.profileError);
    },
  });

  const handleInputChange = (state: Dispatch<SetStateAction<string>>) =>
    (event: ChangeEvent<HTMLInputElement>) => state(event.target.value);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!login.trim() || !password) {
      toast.error(t.auth.fillBothFields);
      return;
    }
    try {
      const data = await emailLoginMutation.mutateAsync();
      if (!data.token) { clearAuthentication(); toast.error(data.message || t.auth.loginFailed); return; }
      if (!data.user?.id) { clearAuthentication(); toast.error(t.auth.incompleteUser); return; }
      TokenStore.setToken(data.token);
      UserIdStore.setUserId(data.user.id);
      navigate("/dashboard");
    } catch {
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      const provider = new GoogleAuthProvider();
      const result   = await signInWithPopup(auth, provider);
      const idToken  = await result.user.getIdToken();
      fetchMeMutation.mutate(idToken);
    } catch (error: unknown) {
      const fe = error as { code?: string; message?: string };
      if (fe.code === "auth/popup-closed-by-user") return;
      toast.error(`${t.auth.firebaseError} ${fe.code || "unknown"} - ${fe.message || t.auth.unknownError}`);
    }
  };

  const isLoading = emailLoginMutation.isPending || fetchMeMutation.isPending;

  return (
    <div className={`connect-page connect-page--${theme}`}>

      <div className="connect-controls">
        <select
          className="connect-lang-select"
          value={language}
          onChange={(e) => setLanguage(e.target.value as Language)}
          aria-label="Language"
        >
          {availableLanguages.map((l) => (
            <option key={l.code} value={l.code}>{l.label} {l.code.toUpperCase()}</option>
          ))}
        </select>

        <button
          className="connect-theme-btn"
          onClick={toggleTheme}
          aria-label={theme === "light" ? t.preferences.darkMode : t.preferences.lightMode}
        >
          {theme === "light" ? (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>
          ) : (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/></svg>
          )}
        </button>
      </div>

      {/* Card */}
      <div className="connect-card">
        <div className="connect-card-top-bar" />

        <div className="connect-card-body">

          {/* Brand */}
          <div className="connect-brand">
           <div className="connect-brand-icon">
  <img 
    src="/logo_notacito.svg" 
    alt="Notacito Logo" 
    style={{ width: 35, height: 35 }} 
  />
</div>
            <span className="connect-brand-name">Notacito</span>
            <span className="connect-brand-tag">Admin</span>
          </div>

          <h1 className="connect-title">{t.auth.loginTitle}</h1>
          <p className="connect-subtitle">{t.auth.subtitle}</p>

          <form onSubmit={handleSubmit} className="connect-form">
            <div className="connect-field">
              <label htmlFor="email">{t.auth.email}</label>
              <input
                id="email"
                type="email"
                name="email"
                placeholder="admin@company.com"
                value={login}
                onChange={handleInputChange(setLogin)}
                autoComplete="email"
                required
              />
            </div>

            <div className="connect-field">
              <label htmlFor="password">{t.auth.password}</label>
              <input
                id="password"
                type="password"
                name="password"
                placeholder="••••••••"
                value={password}
                onChange={handleInputChange(setPassword)}
                autoComplete="current-password"
                required
              />
            </div>

            <button type="submit" className="connect-btn-primary" disabled={isLoading}>
              {emailLoginMutation.isPending ? t.auth.signingIn : t.auth.login}
            </button>
          </form>

          <div className="connect-divider">
            <span className="connect-divider-line" />
            <span className="connect-divider-text">{t.auth.or}</span>
            <span className="connect-divider-line" />
          </div>

          <button
            type="button"
            className="connect-btn-google"
            onClick={handleGoogleSignIn}
            disabled={isLoading}
          >
            {fetchMeMutation.isPending ? (
              t.auth.signingIn
            ) : (
              <>
                <FcGoogle className="connect-google-icon" />
                <span>{t.auth.googleLogin}</span>
              </>
            )}
          </button>

          <div className="connect-register">
            <span>{t.auth.noAccount}</span>
            <Link to="/register">{t.auth.register}</Link>
          </div>

           <div className="connect-register">
            <span>{t.auth.forgotPassword}</span>
            <Link to="/forgot-password">{t.auth.resetPassword}</Link>
          </div>
        </div>
      </div>

      {generateToastContainer()}
    </div>
  );
}

export default ConnectForm;